import { Users, UtensilsCrossed, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

export const HeroTrustBar = () => {
  const { t } = useTranslation();
  const { data: counts } = useQuery({
    queryKey: ['hero-trust-counts'],
    queryFn: async () => {
      // Single RPC replaces 3 separate count HEAD requests — saves ~2 round
      // trips and 2 resource spans on every landing visit.
      const { data, error } = await supabase.rpc('get_hero_trust_counts');
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return {
        members: Number(row?.members ?? 0),
        partners: Number(row?.partners ?? 0),
        cities: Number(row?.cities ?? 0),
      };
    },
    staleTime: 30 * 60 * 1000, // 30 min — counts don't move that fast
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (!counts || (counts.members === 0 && counts.partners === 0 && counts.cities === 0)) {
    return null;
  }

  const stats = [
    { icon: Users, value: counts.members > 0 ? counts.members.toLocaleString() : '—', label: t('landing_extra.members', { count: counts.members }), show: counts.members > 0 },
    { icon: UtensilsCrossed, value: counts.partners > 0 ? counts.partners.toLocaleString() : '—', label: t('landing_extra.partner_venues', { count: counts.partners }), show: counts.partners > 0 },
    { icon: MapPin, value: counts.cities > 0 ? counts.cities.toLocaleString() : '—', label: t('landing_extra.cities', { count: counts.cities }), show: counts.cities > 0 },
  ].filter(s => s.show);

  if (stats.length === 0) return null;

  return (
    <div className="flex justify-center gap-6 md:gap-10">
      {stats.map((stat, i) => (
        <div key={stat.label} className="flex items-center gap-2 text-foreground/60">
          <stat.icon className="h-3.5 w-3.5 text-primary/60" />
          <span className="text-xs font-semibold text-foreground/80 tracking-wide">{stat.value}</span>
          <span className="text-xs text-foreground/50">{stat.label}</span>
          {i < stats.length - 1 && (
            <span className="hidden md:inline-block ml-4 md:ml-6 w-px h-3 bg-foreground/10" />
          )}
        </div>
      ))}
    </div>
  );
};
